/**
 * useShellReload - R and the footer's Reload button.
 *
 * Ammo is combat STATE: a real actor's reload writes only that half, never
 * the sheet. Acting as nobody there is no CombatState, so the live count
 * lives on the scratch slots - the same place firing deducts it from (this
 * branch went missing in the v7 roster refactor and R was a dead key for a
 * month). THE PIPS: a reload is the one action that is not a catalog plate,
 * so it charges the economy itself (v8.331.4). RAW: reloading is not
 * attacking, so a held aim is lost. Extracted from DeathWatchSimulator.
 */
import { useCallback } from 'react';
import { reloadPoints } from '../engine/actions/economy';
import { NO_ACTOR_PROFILE } from '../engine/roster/legacyAdapter';
import { projectLoadout } from '../engine/roster/projection';
import { clearRawAim, dropHeldAim } from './shellCombatant';

/** The scratch rack with one plate's magazine refilled. */
export function refilledScratch(p, index) {
  const slots = [...(p.list?.[NO_ACTOR_PROFILE] ?? [])];
  const slot = slots[index];
  if (!slot) return p;
  slots[index] = { ...slot, ammo: { ...slot.ammo, current: slot.ammo?.max ?? 0 } };
  return { ...p, list: { ...p.list, [NO_ACTOR_PROFILE]: slots } };
}

/** A magazine with a size - the only kind a reload can fill. */
const sized = (a) => typeof a?.max === 'number';

/**
 * The actor's ammo with one weapon's magazine full: the magazine the state
 * holds, else the weapon's own (`slotAmmo`). A weapon the state had no
 * entry for used to get a bare `{ current: 0 }` - "0 of undefined rounds"
 * (overnight fuzz, v8.399.9) - and such an entry is healed the same way.
 * No magazine anywhere: the ammo comes back unchanged.
 */
export function refilledAmmo(weaponAmmo, weaponId, slotAmmo) {
  const magazine = sized(weaponAmmo?.[weaponId]) ? weaponAmmo[weaponId] : slotAmmo;
  if (!sized(magazine)) return weaponAmmo;
  return { ...weaponAmmo, [weaponId]: { ...magazine, current: magazine.max } };
}

/** The actor's weapon's magazine refilled; false when the plate has no weapon behind it. */
function refillActor(actor, index, updateCombatantState) {
  const { slots, weaponIds } = projectLoadout(actor);
  const weaponId = weaponIds[index];
  if (!weaponId) return false;
  const slotAmmo = slots[index]?.ammo;
  updateCombatantState(actor.id, (st) => ({ ...st, weaponAmmo: refilledAmmo(st.weaponAmmo, weaponId, slotAmmo) }));
  return true;
}

/** The reload's log line. */
export function reloadText(slot) {
  return '🔄 Reloaded ' + slot.weaponName + ' (' + (slot.ammo?.reloadAction || 'Full') + ' Action).';
}

/** One reload of the armed plate, in the inline version's order: pips, ammo, line, aim. */
function reload(d) {
  const slot = d.selectedSlot === null ? null : d.currentSlots[d.selectedSlot];
  if (!slot) return;
  if (d.actor) d.spendTurnRef.current?.(d.actor.id, reloadPoints(slot.ammo?.reloadAction));
  if (!d.actor) d.setAtkProfiles((p) => refilledScratch(p, d.selectedSlot));
  else if (!refillActor(d.actor, d.selectedSlot, d.updateCombatantState)) return;
  d.addNarrative(d.activeTab, { type: 'info', text: reloadText(slot), timestamp: Date.now() });
  if (d.actor) dropHeldAim(d.actor, 'reloading is not attacking.', d);
  else clearRawAim(d.rawAttackToggles, d.setRawAttackToggles);
}

/**
 * @param {object} args - selectedSlot, currentSlots, actor, activeTab,
 *   rawAttackToggles, spendTurnRef, and the writers: setAtkProfiles,
 *   updateCombatantState, addNarrative, addRollNarrative, setRawAttackToggles.
 *
 * Over the length target on purpose: bindings and a dependency list.
 */
export default function useShellReload(args) {
  const { selectedSlot, currentSlots, actor, activeTab, rawAttackToggles, spendTurnRef } = args;
  const { setAtkProfiles, updateCombatantState, addNarrative, addRollNarrative, setRawAttackToggles } = args;
  return useCallback(
    () =>
      reload({
        selectedSlot,
        currentSlots,
        actor,
        activeTab,
        rawAttackToggles,
        spendTurnRef,
        setAtkProfiles,
        updateCombatantState,
        addNarrative,
        addRollNarrative,
        setRawAttackToggles,
      }),
    [
      setRawAttackToggles,
      spendTurnRef,
      selectedSlot,
      currentSlots,
      actor,
      updateCombatantState,
      setAtkProfiles,
      activeTab,
      addNarrative,
      addRollNarrative,
      rawAttackToggles,
    ],
  );
}
